import { z } from 'zod'

/**
 * What's New — the release model (docs/whats-new.md).
 *
 * Pure, and zod-only on purpose: the draft script's tests import this schema
 * to prove the file they write is the file the console reads. Which areas
 * shipped is a BUILD fact the caller supplies (`AreaPredicate`); this module
 * never learns about the registry.
 */

export const RELEASE_KINDS = ['feature', 'enhancement', 'fix'] as const
export type ReleaseKind = (typeof RELEASE_KINDS)[number]

export const RELEASE_KIND_LABEL: Record<ReleaseKind, string> = {
  feature: 'Feature',
  enhancement: 'Enhancement',
  fix: 'Fix',
}

/** The area for a shell-wide change — always shipped. */
export const PLATFORM_AREA = 'platform'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const ReleaseEntrySchema = z
  .object({
    kind: z.enum(RELEASE_KINDS),
    text: z.string().trim().min(1).max(280),
    areas: z.array(z.string().min(1)).min(1),
  })
  .strict()

export const ReleaseSchema = z
  .object({
    date: z.string().regex(ISO_DATE),
    entries: z.array(ReleaseEntrySchema).min(1),
  })
  .strict()

export const WhatsNewFileSchema = z
  .object({
    /** The last commit the draft script summarised (full sha). */
    cursor: z.string().regex(/^[0-9a-f]{40}$/),
    releases: z.array(ReleaseSchema),
  })
  .strict()
  .refine((f) => new Set(f.releases.map((r) => r.date)).size === f.releases.length, {
    message: 'one release per date — merge same-day entries',
  })

export type ReleaseEntry = z.infer<typeof ReleaseEntrySchema>
export type Release = z.infer<typeof ReleaseSchema>
export type WhatsNewFile = z.infer<typeof WhatsNewFileSchema>

/** @throws ZodError — an invalid file is a build defect, loud at load. */
export function parseWhatsNew(raw: unknown): WhatsNewFile {
  return WhatsNewFileSchema.parse(raw)
}

export type AreaPredicate = (area: string) => boolean

/**
 * What this build may show, newest date first. An entry survives only when
 * EVERY area it names shipped — a line about a shipped page and a held-back
 * one would leak the second. A date left with nothing is dropped.
 */
export function visibleReleases(releases: readonly Release[], shipped: AreaPredicate): Release[] {
  return releases
    .map((r) => ({ ...r, entries: r.entries.filter((e) => e.areas.every(shipped)) }))
    .filter((r) => r.entries.length > 0)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
}

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
})

/** `2026-09-28` → `Sep 28, 2026`, in UTC so no zone shifts the day. */
export function formatReleaseDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number)
  return DATE_FORMAT.format(new Date(Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1)))
}
